import React, { useState, useRef } from 'react';
import ReCAPTCHA from 'react-google-recaptcha';
import { useTranslation } from 'react-i18next';
import { loadStripe } from '@stripe/stripe-js';

const RegisterPage = () => {
  const { t } = useTranslation();
  const formRef = useRef(null);
  const [formData, setFormData] = useState({
    name: '',
    lastName: '',
    email: '',
    confirmEmail: '',
    password: '',
    confirmPassword: '',
    houseStreet: '',
    district: '',
    zipCode: '',
    city: '',
    country: '',
    telephone: '',
    preferredLanguage: '',
    membershipCategory: '',
    autoRenew: true,
    adminInviteCode: '', // New state for admin invite code
  });
  const [currentStep, setCurrentStep] = useState(0);
  const [captchaVerified, setCaptchaVerified] = useState(false);
  const [registrationSuccess, setRegistrationSuccess] = useState(false);
  const [showPaymentDetails, setShowPaymentDetails] = useState(false);

  const stripePromise = loadStripe('pk_test_51NynvIARs3OWbuo5dsUooihBcJ5urTjPX1RXkjDXzut8wg1Bhbq1qMyYvF5RUQpcMnXhyrz8G31SkmQJXheTij3g00bTfGd6zp');

  const steps = [
    { name: t('Name'), type: 'text', key: 'name' },
    { name: t('Last Name / Surname'), type: 'text', key: 'lastName' },
    { name: t('Email Address'), type: 'email', key: 'email' },
    { name: t('Confirm Your Email Address'), type: 'email', key: 'confirmEmail' },
    { name: t('Choose a Password'), type: 'password', key: 'password' },
    { name: t('Confirm Your Password'), type: 'password', key: 'confirmPassword' },
    { name: t('House/ Building No. and Street Name'), type: 'text', key: 'houseStreet' },
    { name: t('District'), type: 'text', key: 'district' },
    { name: t('Zip Code'), type: 'text', key: 'zipCode' },
    { name: t('City'), type: 'text', key: 'city' },
    { name: t('Country'), type: 'text', key: 'country' },
    { name: t('Telephone Number'), type: 'tel', key: 'telephone' },
    { name: t('Preferred Language'), type: 'select', key: 'preferredLanguage', options: [
      { value: 'en', label: 'English' },
      { value: 'es', label: 'Español' },
      { value: 'fr', label: 'Français' },
      { value: 'de', label: 'Deutsch' },
      { value: 'zh', label: '简体中文' },
      { value: 'hi', label: 'हिन्दी' },
      { value: 'ar', label: 'العربية' },
      { value: 'pt', label: 'Português (Brasil)' },
      { value: 'ru', label: 'Русский' },
      { value: 'bn', label: 'বাংলা' },
      { value: 'ja', label: '日本語' },
      { value: 'ko', label: '한국어' },
      { value: 'it', label: 'Italiano' },
      { value: 'tr', label: 'Türkçe' },
      { value: 'vi', label: 'Tiếng Việt' },
      { value: 'id', label: 'Bahasa Indonesia' },
      { value: 'ur', label: 'اردو' },
      { value: 'fa', label: 'فارسی' },
      { value: 'th', label: 'ไทย' },
      { value: 'pl', label: 'Polski' },
      { value: 'nl', label: 'Nederlands' },
      { value: 'sw', label: 'Kiswahili' },
      { value: 'no', label: 'Norsk' },
      { value: 'ms', label: 'Bahasa Melayu' },
      { value: 'my', label: 'မြန်မာ' },
    ] },
    { name: t('Membership Category'), type: 'radio', key: 'membershipCategory', options: [
      { value: 'pay-as-you-go', label: t('Pay as you go') },
      { value: 'monthly', label: t('Monthly Membership (19.99 USD/Billed Monthly)') },
      { value: 'yearly', label: t('Yearly Membership (14.99 USD/Billed Yearly)') }
    ] },
  ];

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData({
      ...formData,
      [name]: type === 'checkbox' ? checked : value,
    });
  };

  const handleNextStep = () => {
    if (currentStep < steps.length - 1) {
      setCurrentStep(currentStep + 1);
      setTimeout(() => {
        if (formRef.current) {
          // Scroll to the top for the first few steps, then scroll to the bottom for later steps
          if (currentStep >= 7) {
            formRef.current.scrollIntoView({ behavior: 'smooth', block: 'end' });
          } else {
            formRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
          }
        }
      }, 100);
    } else {
      setRegistrationSuccess(true);
    }
  };

  const handleCaptcha = async (token) => {
    setCaptchaVerified(!!token);

    if (token) {
        try {
            const response = await fetch('/api/verify-recaptcha', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({ token }),
            });

            const data = await response.json();
            if (!data.success) {
                alert('reCAPTCHA verification failed. Please try again.');
                setCaptchaVerified(false);
            }
        } catch (error) {
            console.error('Error verifying reCAPTCHA:', error);
            alert('An error occurred while verifying reCAPTCHA. Please try again.');
            setCaptchaVerified(false);
        }
    }
};

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!captchaVerified) {
      alert('Please verify the CAPTCHA.');
      return;
    }
    const payload = {
      username: formData.email,
      password: formData.password,
      tier: formData.membershipCategory === 'admin' ? 'Admin' : (formData.membershipCategory === 'monthly' ? 'Tier2' : (formData.membershipCategory === 'yearly' ? 'Tier3' : 'Tier1')),
      role: formData.membershipCategory === 'admin' ? 'admin' : 'member',
      admin_invite_code: formData.membershipCategory === 'admin' ? formData.adminInviteCode : undefined
    };
    // ...submit payload to backend registration endpoint...
    console.log('Form submitted:', payload);
  };

  const handleMembershipChange = (e) => {
    handleChange(e);
    setShowPaymentDetails(e.target.value !== '');
  };

  const handleStripePayment = async () => {
    const stripe = await stripePromise;
    const response = await fetch('/create-checkout-session', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        membershipCategory: formData.membershipCategory,
      }),
    });
    const session = await response.json();
    await stripe.redirectToCheckout({ sessionId: session.id });
  };

  const isCurrentStepFilled = () => {
    const step = steps[currentStep];
    if (step.type === 'radio') {
      return !!formData[step.key];
    }
    if (step.type === 'select') {
      return !!formData[step.key];
    }
    return formData[step.key] && formData[step.key].toString().trim() !== '';
  };

  return (
    <div className="relative py-20 min-h-screen bg-gray-100 overflow-hidden">
      {/* Single, high-quality luxury watch background image */}
      <div className="absolute inset-0 z-0">
        <img
          src="/images/RegisterPagePicture.jpg"
          alt="Luxury watch background"
          className="w-full h-full object-cover object-center opacity-60"
          style={{ minHeight: '100vh' }}
        />
        <div className="absolute inset-0 bg-white bg-opacity-60"></div>
      </div>
      <div className="relative z-10 container mx-auto py-10 px-6">
        <h1 className="text-3xl font-bold mb-6">{t('register')}</h1>
        {registrationSuccess ? (
          <p className="text-green-600">{t('Registration Successful. We have sent you an Email and please confirm your email address by clicking through the link we have provided in our email.')}</p>
        ) : (
          <form onSubmit={handleSubmit} className="max-w-lg mx-auto" ref={formRef}>
            {steps.slice(0, currentStep + 1).map((step, index) => (
              <div className="mb-4" key={index}>
                <label className="block text-gray-700 mb-2">{step.name}</label>
                {step.type === 'textarea' ? (
                  <textarea
                    name={step.key}
                    value={formData[step.key]}
                    onChange={handleChange}
                    className="w-full border rounded-lg px-3 py-2"
                    required
                  ></textarea>
                ) : step.type === 'select' ? (
                  <select
                    name={step.key}
                    value={formData[step.key]}
                    onChange={handleChange}
                    className="w-full border rounded-lg px-3 py-2"
                    required
                  >
                    <option value="">{t('Select a language')}</option>
                    {step.options.map((option) => (
                      <option key={option.value} value={option.value}>{option.label}</option>
                    ))}
                  </select>
                ) : step.type === 'radio' ? (
                  step.options.map((option) => (
                    <div key={option.value} className="mb-2">
                      <label>
                        <input
                          type="radio"
                          name={step.key}
                          value={option.value}
                          checked={formData[step.key] === option.value}
                          onChange={handleMembershipChange}
                          className="mr-2"
                        />
                        {option.label}
                      </label>
                    </div>
                  ))
                ) : (
                  <input
                    type={step.type}
                    name={step.key}
                    value={formData[step.key]}
                    onChange={handleChange}
                    className="w-full border rounded-lg px-3 py-2"
                    required
                  />
                )}
              </div>
            ))}
            {formData.membershipCategory === 'monthly' || formData.membershipCategory === 'yearly' ? (
              <div className="mb-4">
                <label>
                  <input
                    type="checkbox"
                    name="autoRenew"
                    checked={formData.autoRenew}
                    onChange={handleChange}
                    className="mr-2"
                  />
                  {t('Automatically Renew my Subscription')}
                </label>
              </div>
            ) : null}
            {formData.membershipCategory === 'admin' && (
              <div className="mb-4">
                <label className="block text-gray-700 mb-2">Admin Invite Code</label>
                <input
                  type="text"
                  name="adminInviteCode"
                  value={formData.adminInviteCode || ''}
                  onChange={handleChange}
                  className="w-full border rounded-lg px-3 py-2"
                  required
                />
              </div>
            )}
            {formData.membershipCategory && showPaymentDetails && (
              <div className="mb-4">
                <label className="block text-gray-700 mb-2">Payment Details</label>
                <select
                  name="paymentDetails"
                  value={formData.paymentDetails || ''}
                  onChange={handleChange}
                  className="w-full border rounded-lg px-3 py-2"
                  required
                >
                  <option value="">Select Payment Method</option>
                  <option value="credit-card">Credit Card</option>
                  <option value="stripe">Stripe</option>
                </select>
              </div>
            )}
            {currentStep < steps.length - 1 ? (
              <button
                type="button"
                onClick={handleNextStep}
                className="bg-blue-600 text-white py-2 px-4 rounded-lg hover:bg-blue-700 transition"
                disabled={!isCurrentStepFilled()}
              >
                {t('Next')}
              </button>
            ) : (
              <div>
                <div className="mb-6">
                  <ReCAPTCHA
                    sitekey="6LcSMUcrAAAAAEnSW0ovt96kI_EifDvzRzDMUSMm"
                    onChange={handleCaptcha}
                  />
                </div>
                <button
                  type="submit"
                  className="bg-blue-600 text-white py-2 px-4 rounded-lg hover:bg-blue-700 transition"
                  disabled={!captchaVerified}
                >
                  {t('Register')}
                </button>
              </div>
            )}
          </form>
        )}
      </div>
    </div>
  );
};

export default RegisterPage;